import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { defineMessages, FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl';
import type { EditorTarget, EditorSaveOutcome } from './FlowTestResultView';
import type { ReviewTestInvocation } from './testcaseWireTypes';

export type DeriveTestHandler = (name: string, reason: string, source: EditorTarget) => Promise<EditorSaveOutcome>;

type Props = InjectedIntlProps & {
    testInvocation: ReviewTestInvocation;
    disabled?: boolean;
    onDerive: DeriveTestHandler;
};
type State = {
    open: boolean;
    name: string;
    reason: string;
    source: EditorTarget | null;
    saving: boolean;
    error: string | null;
};
const messages = defineMessages({
    defaultName: { id: 'snapshotTesting.derivedDefaultName', defaultMessage: '{name} (derived)' },
    failed: { id: 'snapshotTesting.deriveFailed', defaultMessage: 'Could not confirm creation. Check the testcase list before retrying to avoid duplicates.' },
});

export class CreateDerivedTestButton extends React.Component<Props, State> {
    state: State = { open: false, name: '', reason: '', source: null, saving: false, error: null };
    private disposed = false;
    private saving = false;
    private generation = 0;

    componentWillUnmount() { this.disposed = true; }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.testInvocation.id !== this.props.testInvocation.id) {
            this.generation++;
            this.saving = false;
            this.setState({ open: false, source: null, saving: false, error: null });
        }
    }

    open = () => {
        if (this.props.disabled || this.saving) return;
        const source = this.props.testInvocation;
        this.generation++;
        this.setState({
            open: true,
            name: this.props.intl.formatMessage(messages.defaultName, { name: source.name }),
            reason: '',
            source: { id: source.id, realId: source.realId, version: source.version },
            error: null,
        });
    };

    close = () => {
        if (!this.saving) this.setState({ open: false });
    };

    save = async () => {
        const { source, name, reason } = this.state;
        if (this.saving || this.props.disabled || !source || !name.trim()
            || source.id !== this.props.testInvocation.id || source.version !== this.props.testInvocation.version) return;
        this.saving = true;
        const generation = this.generation;
        this.setState({ saving: true, error: null });
        let outcome: EditorSaveOutcome;
        try {
            outcome = await this.props.onDerive(name.trim(), reason.trim(), source);
        } catch {
            outcome = { ok: false, error: this.props.intl.formatMessage(messages.failed) };
        }
        if (this.disposed || generation !== this.generation) return;
        this.saving = false;
        this.setState({ saving: false, open: !outcome.ok, error: outcome.ok ? null : outcome.error || this.props.intl.formatMessage(messages.failed) });
    };

    render() {
        const stale = !!this.state.source && this.state.source.version !== this.props.testInvocation.version;
        return <span>
            <Button onClick={this.open} disabled={this.props.disabled || this.state.saving}>
                <i className="fa fa-code-fork" /> <FormattedMessage id="snapshotTesting.createDerived" defaultMessage="Create Derived Test" />
            </Button>
            <Modal show={this.state.open} onHide={this.close}>
                <Modal.Header closeButton={!this.state.saving}>
                    <Modal.Title><FormattedMessage id="snapshotTesting.createDerived" defaultMessage="Create Derived Test" /></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><FormattedMessage id="snapshotTesting.deriveExplanation" defaultMessage="Create an independent, permanent testcase. The saved input, baseline, flow binding and snapshot policy are copied; run history is not. Nothing runs automatically." /></p>
                    <p><FormattedMessage id="snapshotTesting.deriveBaselineWarning" defaultMessage="The copied baseline is a starting point, not approval of your new scenario. Existing Edit Snapshot restrictions still apply." /></p>
                    <label htmlFor="snapshot-derived-name"><FormattedMessage id="snapshotTesting.derivedName" defaultMessage="Test name" /></label>
                    <input id="snapshot-derived-name" className="form-control" value={this.state.name} disabled={this.state.saving}
                        onChange={event => this.setState({ name: event.target.value })} />
                    <label htmlFor="snapshot-derived-reason"><FormattedMessage id="snapshotTesting.derivedReason" defaultMessage="Reason or task reference (optional)" /></label>
                    <textarea id="snapshot-derived-reason" className="form-control" rows={2} value={this.state.reason} disabled={this.state.saving}
                        onChange={event => this.setState({ reason: event.target.value })} />
                    {stale && <p role="alert"><FormattedMessage id="snapshotTesting.deriveStale" defaultMessage="The source version changed. Close this dialog, review the source and reopen it." /></p>}
                    {this.state.error && <p role="alert">{this.state.error}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.close} disabled={this.state.saving}><FormattedMessage id="common.cancel" defaultMessage="Cancel" /></Button>
                    <Button bsStyle="primary" onClick={this.save} disabled={this.state.saving || stale || this.props.disabled || !this.state.name.trim()}>
                        <FormattedMessage id="snapshotTesting.createDerived" defaultMessage="Create Derived Test" />
                    </Button>
                </Modal.Footer>
            </Modal>
        </span>;
    }
}

export default injectIntl(CreateDerivedTestButton);
